import type { SeoDto } from "../types";
import { computed, useCurrentCulture, useHead, useRequestURL } from "#imports";

export type SeoData = SeoDto;

interface HeadData {
  title?: string | null;
  meta: {
    key?: string;
    name?: string;
    property?: string;
    content?: string | null;
  }[];
  link: {
    rel: string;
    href: string;
  }[];
}

export function useSeo ( seoData: SeoDto | null | undefined ) {
  if ( !seoData ) {
    return;
  }

  const url = useRequestURL();

  const isInternal = computed( () => {
    return url.host.includes( "edcowebsites" );
  } );

  const robots = [
    seoData.noIndex || isInternal.value ? "noindex" : "index",
    seoData.noFollow || isInternal.value ? "nofollow" : "follow",
  ].join( ", " );

  const culture = useCurrentCulture();

  const headData: HeadData = {
    title: seoData.title,
    meta: [
      { key: "og:title", property: "og:title", content: seoData.title },
      { key: "og:description", property: "og:description", content: seoData.description },
      { key: "og:locale", property: "og:locale", content: culture.value ?? "en" },
      { key: "og:type", property: "og:type", content: "website" },
      { key: "og:url", property: "og:url", content: url.href },

      { key: "twitter:card", name: "twitter:card", content: "summary" },
      // {name: "twitter:site", content: "Apollo"}, // todo: twitter username, should probably come from global site seo data
      { key: "twitter:url", name: "twitter:url", content: url.href },
      { key: "twitter:title", name: "twitter:title", content: seoData.title },
      { key: "twitter:description", name: "twitter:description", content: seoData.description },

      { key: "description", name: "description", content: seoData.description },
      { key: "keywords", name: "keywords", content: seoData.keywords?.join( ", " ) },
      { key: "robots", name: "robots", content: robots },
    ],
    link: [],
  };

  if ( seoData.socialImage ) {
    const imageUrl = new URL( seoData.socialImage.url, url );
    headData.meta.push( {
      key: "og:image",
      property: "og:image",
      content: imageUrl.href,
    },
    {
      key: "twitter:image",
      name: "twitter:image",
      content: imageUrl.href,
    } );
  }

  if ( seoData.canonicalUrl ) {
    headData.link.push( {
      rel: "canonical",
      href: seoData.canonicalUrl,
    } );
  }

  useHead( headData );
}
